import {
  GrpcResponseError,
  useGrpcRequestMutation,
} from '@aboutyou/data-fetcher';
import {AuthenticationService_GetSSOButtonsLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/authentication/v1/service.grpc.lazy';
import {
  BlurredOverlay,
  LoadingSpinner,
  RenderInPortal,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {SSO_CALLBACK_ROUTE} from '../../../SSOCallbackMiddleware/constants';
import {useAuthLogger} from '../../../hooks/useAuthLogger';
import {PopupOverlay} from './PopupOverlay';
import {useSSOCallbackHandler} from './useSSOCallbackHandler';

export const useAutoSignInWithSSO = () => {
  const mutation = useGrpcRequestMutation(
    AuthenticationService_GetSSOButtonsLazy,
  );
  const {logInstanceId, Log} = useAuthLogger();

  const {ssoOpener, isAuthenticating} = useSSOCallbackHandler({
    isSignUp: true,
    disableSuccessToast: true,
    onError: null, // will be set on the signIn call ↙️
    onSuccess: null, // will be set on the signIn call ↙️
  });

  const signIn = ({
    idp,
    onError,
    onSuccess,
  }: {
    idp: 'klarna';
    onError: (e: string) => void;
    onSuccess: () => void;
  }) => {
    /**
     * we need to create the popup before starting the async operation
     * to avoid the popup being blocked by the browser.
     * We create a blank popup and set the location later when we have the URL
     *
     * ssoPopup.open will set the location of the popup
     */
    const popupInstance = ssoOpener.createPopup();

    return mutation
      .mutateAsync({
        logInstanceId,
        redirectPath: `${window.location.origin}${SSO_CALLBACK_ROUTE}`,
      })
      .then(response => {
        const ssoButton = response.ssoButtons.find(
          btn => btn.idp.toLowerCase() === idp.toLowerCase(),
        );

        if (ssoButton) {
          ssoOpener.open({
            // we want to open forced SignIn with Klarna as popup for desktop and mobile
            // to simplify the flow and avoid full page redirects
            openAsPopup: true,
            ssoButton,
            popupInstance,
            onError,
            onSuccess,
          });
        } else {
          onError(`No SSO button found for idp: ${idp}`);
        }
      })
      .catch(error => {
        const code = (error as GrpcResponseError)?.code ?? 0;
        const message = (error as GrpcResponseError)?.message || 'NA';

        Log({
          event: 'sso-fe-error',
          state: null,
          attributes: {
            reason: 'get-sso-buttons',
            code,
            error_detail: `${message}:${code}`,
          },
        });

        onError(message);
        // Close the popup if it was opened
        if (popupInstance && !popupInstance.closed) {
          popupInstance.close();
        }
      });
  };

  return {
    signIn,
    RenderLoadingState: (
      <>
        {isAuthenticating && (
          <RenderInPortal portalId="sso-buttons-view-portal">
            <StyledOverlayWrapper>
              <LoadingSpinner color="white" />
            </StyledOverlayWrapper>
          </RenderInPortal>
        )}

        <PopupOverlay
          isOpen={ssoOpener.isOpen}
          onClick={ssoOpener.focus}
          onClose={ssoOpener.close}
        />
      </>
    ),
  };
};

const StyledOverlayWrapper = styled(BlurredOverlay)`
  z-index: ${Z_INDEXES.TO_THE_TOP};
`;
